/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/datePicker.scss';

import React from 'react';
import {PureComponent} from 'react';
import {DateNumParts, PickerKey} from "../dateField";
import {ControlContext} from "../../../util/controlContext";
import {colorIsWhite, convertColor, editColor} from "../../../util/colorUtil";
import {voidFunction} from "../../../util/controlUtil";
import classNames from "classnames";
import {Moment, unitOfTime} from 'moment';
import {DateTimeParts} from "../dateFieldController";
import {toMoment} from '../../../util/momentUtil';
import AwesomeIcon from '../../../icon/awesomeIcon';

interface Props {
    onChange: (key: PickerKey, value: number, add?: boolean) => VoidFunction;
    getCurrentMoment: (value?: DateNumParts) => Moment;
    pickerValue: DateNumParts;
    dateParts: DateTimeParts;
    disableAfter?: Date | Moment | string;
    disableBefore?: Date | Moment | string;
    disableFuture?: boolean;
    disablePast?: boolean;
}

export default class DatePicker extends PureComponent<Props> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    onClickReset = (type: 'year' | 'month') => () => {
        const {onChange} = this.props, today = toMoment();
        let key: PickerKey, value: number;
        if (type === 'year') {
            key = PickerKey.YEAR;
            value = today.year();
        } else {
            key = PickerKey.MONTH;
            value = today.month();
        }
        onChange(key, value, false)();
    };

    onLeaveDay = (hoverAble: boolean) => (evt: React.MouseEvent<HTMLLabelElement>) => {
        if (hoverAble) {
            const target = evt.target as HTMLLabelElement;
            target.style.backgroundColor = 'transparent';
            target.style.color = convertColor('color-gray-header');
        }
    };

    onEnterDay = (hoverAble: boolean) => (evt: React.MouseEvent<HTMLLabelElement>) => {
        if (hoverAble) {
            const target = evt.target as HTMLLabelElement;
            target.style.backgroundColor = editColor(.1, this.getBaseColor(false));
            target.style.color = this.getContrastColor(false);
        }
    };

    getBaseColor = (allowWhite: boolean = true): string => {
        let color = convertColor(this.context.baseColor);
        if (!allowWhite && colorIsWhite(color)) color = convertColor(this.context.contrastColor);
        return color;
    };

    getContrastColor = (allowWhiteBase: boolean = true): string => {
        if (!allowWhiteBase) {
            const base = this.getBaseColor(true);
            if (colorIsWhite(base)) return base;
        }
        return convertColor(this.context.contrastColor);
    };

    getMonthLabel = (index: number): string =>
        toMoment().month(index).format('MMMM');

    getDaysToRender = (): { day: number, isCurrentMonth: boolean }[] => {
        const daysToRender: { day: number, isCurrentMonth: boolean }[] = [],
            currentMoment = this.props.getCurrentMoment(), dayPadding = currentMoment.date(1).day() - 1,
            days = currentMoment.daysInMonth(), maxPrevMonth = currentMoment.subtract(1, 'month').daysInMonth();

        // Add days to pad begin weekdays
        for (let i = dayPadding; i > 0; i--)
            daysToRender.push({day: maxPrevMonth - (i - 1), isCurrentMonth: false});

        for (let day = 1; day <= days; day++) daysToRender.push({day, isCurrentMonth: true});

        // Add days to pad end weekdays
        for (let i = 1; daysToRender.length / 7 < 5; i++)
            daysToRender.push({day: i, isCurrentMonth: false});

        return daysToRender;
    };

    isDateDisabled = (date: Moment, granularity: unitOfTime.StartOf = 'd'): boolean => {
        const {disableAfter, disableBefore, disableFuture, disablePast} = this.props, now = toMoment();
        if (disablePast && date.isBefore(now, granularity)) return true;
        if (disableFuture && date.isAfter(now, granularity)) return true;
        if (disableAfter && date.isAfter(toMoment(disableAfter), granularity)) return true;
        return !!(disableBefore && date.isBefore(toMoment(disableBefore), granularity));
    };

    isDayCurrent = (day: number) => {
        const {pickerValue, dateParts, getCurrentMoment} = this.props, pickerCurrent = getCurrentMoment(),
            currentMonth = dateParts.month.includes('m') ? toMoment().month() : Number(dateParts.month) - 1;
        return pickerCurrent.month() === currentMonth && day === Number(pickerValue.day);
    };

    renderYearContainer = () => {
        const {pickerValue, onChange} = this.props, year = pickerValue.year, baseColor = this.getBaseColor(),
            contrast = this.getContrastColor();
        return (
            <div className='dvr-year-container' style={{backgroundColor: baseColor}}>
                <AwesomeIcon name='chevron-left' className='dvr-year-toggle' style={{color: contrast}}
                             onClick={onChange(PickerKey.YEAR, -1)}/>
                <label className='dvr-year-label' style={{color: contrast}}
                       onClick={this.onClickReset('year')}>{year}</label>
                <AwesomeIcon name='chevron-right' className='dvr-year-toggle' style={{color: contrast}}
                             onClick={onChange(PickerKey.YEAR, 1)}/>
            </div>
        )
    };

    renderMonthContainer = () => {
        const {pickerValue, onChange} = this.props, month = pickerValue.month;
        return (
            <div className='dvr-month-container'>
                <AwesomeIcon name='chevron-left' className='dvr-month-toggle' onClick={onChange(PickerKey.MONTH, -1)}/>
                <label className='dvr-month-label' onClick={this.onClickReset('month')}>{this.getMonthLabel(
                    month)}</label>
                <AwesomeIcon name='chevron-right' className='dvr-month-toggle' onClick={onChange(PickerKey.MONTH, 1)}/>

            </div>
        )
    };

    renderDaysContainer = () => {
        const daysToRender = this.getDaysToRender(), weekDays: string[] = [];
        for (let i = 1; i <= 7; i++)
            weekDays.push(toMoment().day(i).format('dd'));
        return (
            <div className='dvr-days-container'>
                {weekDays.map((day: string, index: number) => (
                    <div key={index} className='dvr-day-container'>
                        <label className='weekday-label'>{day}</label>
                    </div>
                ))}
                {daysToRender.map(this.renderDay)}
            </div>
        )
    };

    renderDay = (dayData: { day: number, isCurrentMonth: boolean }, index: number) => {
        const {day, isCurrentMonth} = dayData, {onChange} = this.props,
            dayMoment = this.props.getCurrentMoment().date(day),
            isDisabled = this.isDateDisabled(dayMoment) || !isCurrentMonth,
            isCurrent = this.isDayCurrent(day) && isCurrentMonth, isHoverAble = !isCurrent && isCurrentMonth,
            key: string = isCurrentMonth ? `day-${day}-${index}` : `other-day-${day}-${index}`;

        return (
            <div key={key}
                 className={classNames('dvr-day-container', isCurrent && 'current', isDisabled && 'disabled',
                     !isCurrentMonth && 'other-month')}>
                <label className='day-label'
                       onMouseEnter={isDisabled ? voidFunction : this.onEnterDay(isHoverAble)}
                       onMouseLeave={isDisabled ? voidFunction : this.onLeaveDay(isHoverAble)}
                       style={isCurrent ?
                           {color: this.getContrastColor(false), backgroundColor: this.getBaseColor(false)} :
                           {}} onClick={isDisabled ? voidFunction :
                    onChange(PickerKey.DAY, day, false)}>{day}</label>
            </div>
        )
    };

    render = () => {
        return (
            <div className='datepicker-container'>
                {this.renderYearContainer()}
                {this.renderMonthContainer()}
                {this.renderDaysContainer()}
            </div>
        )
    };
}